import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { setContextSearchText } from "../../actions/projects";
import { route } from "../../config";

/**
 * Gets the context search scope key.
 * @author Bhavana
 */
const getContextSearchScopeKey = (pathname, activeOrganizationSidebar, teamLeftSelectedTab, userLeftSelectedTab) => {
  if (pathname.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH)) {
    return `${pathname}${activeOrganizationSidebar}`;
  }
  if (pathname.includes(route.PRIVATE_ROUTE.TEAM_SETTING.PATH)) {
    return `${pathname}${teamLeftSelectedTab}`;
  }
  if (pathname.includes(route.PRIVATE_ROUTE.USER_PROFILE_SETTING.PATH)) {
    return `${pathname}${userLeftSelectedTab}`;
  }
  return pathname;
};

/**
 * Resets the context search when the user navigates away from a screen or switches a sub-view that has its own search context (Manage Organization sidebar, settings tabs).
 * @author Bhavana
 */
export const useResetContextSearchOnScopeChange = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const activeOrganizationSidebar = useSelector((s) => s.admin?.activeOrganizationSidebar ?? "");
  const teamLeftSelectedTab = useSelector((s) => s.projectSidebar?.teamLeftSelectedTab ?? "");
  const userLeftSelectedTab = useSelector((s) => s.projectSidebar?.userLeftSelectedTab ?? "");

  const scopeKey = useMemo(() =>
    getContextSearchScopeKey(location.pathname, activeOrganizationSidebar, teamLeftSelectedTab, userLeftSelectedTab),
    [location.pathname, activeOrganizationSidebar, teamLeftSelectedTab, userLeftSelectedTab],
  );

  const prevScopeRef = useRef(null);

  /**
   * Resets the context search when the scope key changes.
   * @author Bhavana
   */
  useEffect(() => {
    const prev = prevScopeRef.current;
    prevScopeRef.current = scopeKey;
    if (prev != null && prev !== scopeKey) {
      dispatch(setContextSearchText(""));
    }
  }, [scopeKey]);
};
